import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { useFormContext } from 'react-hook-form';

import { AnalyticsContext } from '../analytics-dashboard';
import { getSurveyQuestionSets } from 'redux/selectors/analytics';
import { useSyncDropdownValue, questionsToOptions } from './utils';

import { NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import NvFormDropdown from 'shared/components/inputs/nv-form-dropdown';
import Divider from 'shared/components/divider';

// Sub-component for NPS/CSAT section
interface MetricSelectionSectionProps {
  title: string;
  surveyFieldName: string;
  questionFieldName: string;
  surveyOptions: NvDropdownOption[];
  questionOptions: NvDropdownOption[];
  onSurveyChange: (value: NvDropdownOption) => void;
  onQuestionChange: (value: NvDropdownOption) => void;
  className?: string;
}

const MetricSelectionSection = ({
  title,
  surveyFieldName,
  questionFieldName,
  surveyOptions,
  questionOptions,
  onSurveyChange,
  onQuestionChange,
  className = '',
}: MetricSelectionSectionProps) => (
  <div className={className}>
    <div className='bold'>{title}</div>
    <div className='d-flex'>
      <div className='flex-grow-1 text-regular'>
        <NvFormDropdown
          items={surveyOptions}
          name={surveyFieldName}
          className='my-2'
          onChange={onSurveyChange}
          title={t.COURSE_ANALYTICS.PRE_POST.MODAL.SURVEY_SELECT_PLACEHOLDER()}
        />
      </div>
      <div className='flex-grow-1 text-regular ml-5'>
        <NvFormDropdown
          items={questionOptions}
          name={questionFieldName}
          className='my-2'
          onChange={onQuestionChange}
          title={t.COURSE_ANALYTICS.PRE_POST.MODAL.SURVEY_QUESTION_SELECT_PLACEHOLDER()}
        />
      </div>
    </div>
  </div>
);

interface NpsCsatSelectionProps {
  preCatalogId: string;
}

const NpsCsatSelection = ({ preCatalogId }: NpsCsatSelectionProps) => {
  const { watch, setValue } = useFormContext();

  const { analyticsPageType } = useContext(AnalyticsContext);

  // Watch form values
  const preSurveyId = watch('preCourseSurveyId');
  const postSurveyId = watch('postCourseSurveyId');
  const npsSurveyId = watch('npsSurveyId');
  const npsQuestionId = watch('npsQuestionId');
  const csatSurveyId = watch('csatSurveyId');
  const csatQuestionId = watch('csatQuestionId');
  const postCatalogId = watch('postCourseCatalogId');

  const preSurveyQuestions = useSelector(state => state.app.analytics.surveyQuestions?.[preSurveyId]);
  const postSurveyQuestions = useSelector(state => state.app.analytics.surveyQuestions?.[postSurveyId]);

  const allPreSurveys = useSelector((state) =>
    getSurveyQuestionSets(state, analyticsPageType!, preCatalogId),
  );
  const allPostSurveys = useSelector((state) =>
    getSurveyQuestionSets(state, analyticsPageType!, postCatalogId),
  );

  const availableSurveys = useMemo(
    () => [...allPreSurveys, ...(preCatalogId === postCatalogId ? [] : allPostSurveys)]
      .filter((survey) => survey.id === preSurveyId || survey.id === postSurveyId),
    [allPreSurveys, allPostSurveys, preSurveyId, postSurveyId, preCatalogId, postCatalogId],
  );

  const npsSurveyOptions: NvDropdownOption[] = useMemo(
    () => availableSurveys
      .filter((survey) => {
        const questions = survey.id === preSurveyId ? preSurveyQuestions : postSurveyQuestions;
        return (questions || []).some((q) => q.metricEligibleForNps === true);
      })
      .map((survey): NvDropdownOption => ({
        type: 'text',
        id: survey.id,
        value: survey.id,
        text: survey.title,
        class: 'text-wrap',
      })),
    [availableSurveys, preSurveyId, preSurveyQuestions, postSurveyQuestions],
  );

  const csatSurveyOptions: NvDropdownOption[] = useMemo(
    () => availableSurveys
      .filter((survey) => {
        const questions = survey.id === preSurveyId ? preSurveyQuestions : postSurveyQuestions;
        return (questions || []).some((q) => q.metricEligibleForCsat === true);
      })
      .map((survey): NvDropdownOption => ({
        type: 'text',
        id: survey.id,
        value: survey.id,
        text: survey.title,
        class: 'text-wrap',
      })),
    [availableSurveys, preSurveyId, preSurveyQuestions, postSurveyQuestions],
  );

  const npsQuestionOptions = useMemo(() => {
    if (!npsSurveyId) return [];
    if (npsSurveyId === preSurveyId) {
      return questionsToOptions((preSurveyQuestions || []).filter((q) => q.metricEligibleForNps === true));
    }
    return questionsToOptions((postSurveyQuestions || []).filter((q) => q.metricEligibleForNps === true));
  }, [preSurveyQuestions, postSurveyQuestions, npsSurveyId, preSurveyId]);
  const csatQuestionOptions = useMemo(() => {
    if (!csatSurveyId) return [];
    if (csatSurveyId === preSurveyId) {
      return questionsToOptions((preSurveyQuestions || []).filter((q) => q.metricEligibleForCsat === true));
    }
    return questionsToOptions((postSurveyQuestions || []).filter((q) => q.metricEligibleForCsat === true));
  }, [preSurveyQuestions, postSurveyQuestions, csatSurveyId, preSurveyId]);

  // Sync dropdown values
  useSyncDropdownValue(npsSurveyId, 'npsSurveyDropdown', npsSurveyOptions);
  useSyncDropdownValue(npsQuestionId, 'npsQuestionDropdown', npsQuestionOptions);
  useSyncDropdownValue(csatSurveyId, 'csatSurveyDropdown', csatSurveyOptions);
  useSyncDropdownValue(csatQuestionId, 'csatQuestionDropdown', csatQuestionOptions);

  // Handlers
  const handleNpsSurveyChange = (value: NvDropdownOption) => {
    if (value.id === npsSurveyId) return; // No change
    setValue('npsSurveyId', value.id);
    setValue('npsQuestionId', null);
    setValue('npsQuestionDropdown', undefined);
  };

  const handleNpsQuestionChange = (value: NvDropdownOption) => {
    setValue('npsQuestionId', value.id);
  };

  const handleCsatSurveyChange = (value: NvDropdownOption) => {
    if (value.id === csatSurveyId) return; // No change
    setValue('csatSurveyId', value.id);
    setValue('csatQuestionId', null);
    setValue('csatQuestionDropdown', undefined);
  };

  const handleCsatQuestionChange = (value: NvDropdownOption) => {
    setValue('csatQuestionId', value.id);
  };

  if (npsSurveyOptions.length === 0 && csatSurveyOptions.length === 0) {
    return null;
  }
  return (
    <div>
      <div className='mt-5 mb-5'>
        <Divider />
      </div>
      <div className='mb-4 font-weight-bolder'>
        {t.COURSE_ANALYTICS.PRE_POST.MODAL.SURVEY_QUESTIONS()}
      </div>
      {npsSurveyOptions.length > 0 && (
        <MetricSelectionSection
          title={t.COURSE_ANALYTICS.NPS_CSAT_SURVEY.NPS_QUESTION()}
          surveyFieldName='npsSurveyDropdown'
          questionFieldName='npsQuestionDropdown'
          surveyOptions={npsSurveyOptions}
          questionOptions={npsQuestionOptions}
          onSurveyChange={handleNpsSurveyChange}
          onQuestionChange={handleNpsQuestionChange}
        />
      )}
      {csatSurveyOptions.length > 0 && (
        <MetricSelectionSection
          title={t.COURSE_ANALYTICS.NPS_CSAT_SURVEY.CSAT_QUESTION()}
          surveyFieldName='csatSurveyDropdown'
          questionFieldName='csatQuestionDropdown'
          surveyOptions={csatSurveyOptions}
          questionOptions={csatQuestionOptions}
          onSurveyChange={handleCsatSurveyChange}
          onQuestionChange={handleCsatQuestionChange}
          className='mt-4'
        />
      )}
    </div>
  );
};

export default NpsCsatSelection;
